/**
 * enquiry_form.js
 * Alpine factory for the item enquiry form.
 * Auto-bundled by Vite — do NOT import manually.
 *
 * Data contract (emitted inline by enquiry_form.jinja):
 *   <script type="application/json" data-enquiry-form-config>
 *     { hasHcaptcha, hcaptchaSiteKey, hcaptchaSize, hcaptchaContainerId }
 *   </script>
 *   Icon stubs: #enquiry-icon-check, #enquiry-icon-error (hidden spans)
 */

function itemEnquiryForm() {
    let cfg = {};

    function readConfig(root) {
        const script = root?.querySelector?.('[data-enquiry-form-config]')
            || document.querySelector('[data-enquiry-form-config]');
        try { cfg = JSON.parse(script?.textContent || '{}'); } catch (e) { cfg = {}; }
    }

    function iconHtml(id) {
        return document.getElementById(id)?.innerHTML || '';
    }

    function alertHtml(type, body) {
        const icon = iconHtml(type === 'success' ? 'enquiry-icon-check' : 'enquiry-icon-error');
        const color = type === 'success' ? 'success' : 'error';
        return `<div class="mb-4 p-4 bg-${color}/10 border border-${color}/20 rounded-lg">
            <div class="flex items-start">
                <span class="icon text-${color} w-5 h-5 mt-0.5 mr-3">${icon}</span>
                <div>${body}</div>
            </div>
        </div>`;
    }

    function showMsg(id, html) {
        const el = document.getElementById(id);
        if (!el) return;
        el.classList.remove('hidden');
        el.style.display = 'block';
        el.innerHTML = html;
        setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'center' }), 100);
    }

    function responsePayload(event) {
        try { return JSON.parse(event.detail.xhr?.responseText || '{}'); } catch (e) { return {}; }
    }

    return {
        formData: {
            first_name: '', last_name: '', company: '',
            phone: '', email: '', message: '',
            privacy_consent: false, date_time: '',
        },
        errors: {},
        isSubmitting: false,
        isFormValid: false,
        hcaptchaCompleted: !cfg.hasHcaptcha,

        closeModal() {
            const content = this.$refs.enquiryForm?.closest('[id^="htmx-modal-content-"]');
            const modalId = content?.id.replace('htmx-modal-content-', '');
            const triggerRoot = modalId ? document.getElementById(`modal-trigger-${modalId}`)?.closest('.mo-root') : null;
            const alpine = triggerRoot?._x_dataStack?.[0];
            if (alpine && typeof alpine.handleClose === 'function') alpine.handleClose();
            else window.dispatchEvent(new CustomEvent('close-modal'));
        },

        captchaResponseField() {
            return this.$refs.enquiryForm?.querySelector('[name="jinja_data.output_doc_payload.h-captcha-response"]')
                || document.getElementById('hcaptcha-response');
        },

        modalItemPayload() {
            const content = this.$refs.enquiryForm?.closest('[id^="htmx-modal-content-"]');
            if (!content) return {};
            const modalId = content.id.replace('htmx-modal-content-', '');
            const script = document.getElementById(`modal-data-${modalId}-json`);
            try { return JSON.parse(script?.textContent || '{}')?.jinja_data || {}; } catch (e) { return {}; }
        },

        populateItemPayload() {
            const payload = this.modalItemPayload();
            ['item_id', 'item_title', 'item_reference', 'item_image'].forEach(key => {
                const input = this.$refs.enquiryForm?.querySelector(`[name="jinja_data.output_doc_payload.${key}"]`);
                if (input && !input.value && payload[key]) input.value = payload[key];
            });
        },

        getHcaptchaTheme() {
            return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
        },

        loadHcaptcha() {
            if (!cfg.hasHcaptcha || window.hcaptchaScriptLoaded) return;
            window.hcaptchaScriptLoaded = true;
            const script = document.createElement('script');
            script.src = 'https://js.hcaptcha.com/1/api.js?render=explicit';
            script.async = true;
            script.defer = true;
            script.onload = () => window.dispatchEvent(new Event('hcaptchaLoaded'));
            document.head.appendChild(script);
        },

        renderHcaptcha() {
            if (!cfg.hasHcaptcha || typeof hcaptcha === 'undefined') return;
            const container = document.getElementById(cfg.hcaptchaContainerId);
            const resp = this.captchaResponseField();
            if (!container || container.querySelector('iframe')) return;
            this.hcaptchaWidgetId = hcaptcha.render(container, {
                sitekey: cfg.hcaptchaSiteKey,
                size: cfg.hcaptchaSize || 'normal',
                theme: this.getHcaptchaTheme(),
                callback: token => {
                    if (resp) resp.value = token;
                    this.hcaptchaCompleted = true;
                    this.checkFormValidity();
                },
                'expired-callback': () => {
                    if (resp) resp.value = '';
                    this.hcaptchaCompleted = false;
                    this.checkFormValidity();
                },
                'error-callback': () => {
                    if (resp) resp.value = '';
                    this.hcaptchaCompleted = false;
                    this.checkFormValidity();
                },
            });
        },

        checkFormValidity() {
            const d = this.formData;
            let ok = !!(d.first_name?.trim() && d.last_name?.trim() && d.email?.trim() && d.message?.trim() && d.privacy_consent);
            if (cfg.hasHcaptcha && !this.hcaptchaCompleted) ok = false;
            this.isFormValid = ok;
            return ok;
        },

        validateForm() {
            this.errors = {};
            let ok = true;
            if (!this.formData.first_name.trim()) { this.errors.first_name = 'First name is required'; ok = false; }
            if (!this.formData.last_name.trim())  { this.errors.last_name  = 'Last name is required';  ok = false; }
            if (!this.formData.email.trim())       { this.errors.email      = 'Email is required';       ok = false; }
            if (!this.formData.message.trim())     { this.errors.message    = 'Message is required';     ok = false; }
            if (!this.formData.privacy_consent)    { this.errors.privacy_consent = 'You must agree to be contacted'; ok = false; }
            if (this.formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.formData.email)) {
                this.errors.email = 'Please enter a valid email address'; ok = false;
            }
            return ok;
        },

        submitForm(event) {
            event.preventDefault();
            this.populateItemPayload();
            document.getElementById('enquiry-form-messages').innerHTML = '';
            if (!this.validateForm()) { this.showValidationErrors(); return false; }
            if (cfg.hasHcaptcha && !this.captchaResponseField()?.value) {
                this.showCaptchaError(); return false;
            }
            htmx.trigger(this.$refs.enquiryForm, 'enquiry-submit');
            return false;
        },

        setupTimestampInjection() {
            this.$refs.enquiryForm.addEventListener('htmx:configRequest', event => {
                const d = new Date();
                const ord = n => ['th','st','nd','rd'][([11,12,13].includes(n)||n%10>3)?0:n%10];
                const ts =
                    `${d.toLocaleDateString('en-GB',{weekday:'long'})} ` +
                    `${d.getDate()}${ord(d.getDate())} ` +
                    `${d.toLocaleDateString('en-GB',{month:'long'})} ` +
                    `${d.getFullYear()} at ` +
                    `${d.toLocaleTimeString('en-GB',{hour:'2-digit',minute:'2-digit'})}`;
                if (event.detail.parameters['jinja_data.output_doc_payload.date_time'] !== undefined) {
                    event.detail.parameters['jinja_data.output_doc_payload.date_time'] = ts;
                }
            });
        },

        handleFormResponse(event) {
            const msgsEl = document.getElementById('enquiry-form-messages');
            const payload = responsePayload(event);
            const ok = event.detail.successful && payload.success !== false;
            if (ok) {
                showMsg('enquiry-form-messages', alertHtml('success',
                    `<p class="text-base font-medium text-success">${payload.message || 'Thank you. Your enquiry has been sent successfully.'}</p>`));
                setTimeout(() => {
                    msgsEl.innerHTML = ''; msgsEl.classList.add('hidden'); msgsEl.style.display = 'none';
                    this.closeModal();
                    this.resetForm();
                }, 1500);
            } else {
                showMsg('enquiry-form-messages', alertHtml('error',
                    `<p class="text-base font-medium text-error">${payload.message || 'Failed to send enquiry. Please try again.'}</p>`));
            }
        },

        showValidationErrors() {
            const list = Object.values(this.errors).map(e => `<li>${e}</li>`).join('');
            showMsg('enquiry-form-messages', alertHtml('error',
                `<p class="text-base font-medium text-error mb-1">Please fix the following errors:</p>
                 <ul class="text-base text-error list-disc list-inside">${list}</ul>`));
        },

        showCaptchaError() {
            showMsg('enquiry-form-messages', alertHtml('error',
                '<p class="text-base font-medium text-error">Please complete the captcha verification</p>'));
        },

        resetForm() {
            this.formData = { first_name:'', last_name:'', company:'', phone:'', email:'', message:'', privacy_consent:false };
            this.errors = {};
            this.isFormValid = false;

            if (cfg.hasHcaptcha) {
                this.hcaptchaCompleted = false;
                setTimeout(() => {
                    try {
                        const container = document.getElementById(cfg.hcaptchaContainerId);
                        const resp = this.captchaResponseField();
                        if (resp) resp.value = '';
                        if (typeof hcaptcha !== 'undefined') {
                            try { hcaptcha.reset(); } catch (e) {}
                            if (container) {
                                const wid = container.querySelector('iframe')?.getAttribute('data-hcaptcha-widget-id');
                                if (wid) { try { hcaptcha.reset(wid); } catch (e) {} }
                                container.innerHTML = '';
                                hcaptcha.render(container, {
                                    sitekey: cfg.hcaptchaSiteKey,
                                    size: cfg.hcaptchaSize || 'normal',
                                    theme: this.getHcaptchaTheme(),
                                    callback: token => {
                                        this.hcaptchaCompleted = true; this.checkFormValidity();
                                        if (resp) resp.value = token;
                                    },
                                    'expired-callback': () => { this.hcaptchaCompleted = false; this.checkFormValidity(); },
                                });
                            }
                        }
                    } catch (e) { console.error('Error resetting hCaptcha:', e); }
                }, 100);
            }

            const msgs = document.getElementById('enquiry-form-messages');
            if (msgs) { msgs.innerHTML = ''; msgs.classList.add('hidden'); msgs.style.display = 'none'; }
        },

        init() {
            readConfig(this.$el);
            this.hcaptchaCompleted = !cfg.hasHcaptcha;
            this.checkFormValidity();
            this.populateItemPayload();
            this.setupTimestampInjection();
            ['first_name','last_name','email','message','privacy_consent'].forEach(k =>
                this.$watch(`formData.${k}`, () => this.checkFormValidity()));

            if (cfg.hasHcaptcha) {
                this.loadHcaptcha();
                if (typeof hcaptcha !== 'undefined') this.renderHcaptcha();
                window.addEventListener('hcaptchaLoaded', () => this.renderHcaptcha());
                window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => this.resetForm());
                window.addEventListener('hcaptcha-widget-rendered', e => {
                    if (e.detail?.widgetId !== undefined) this.hcaptchaWidgetId = e.detail.widgetId;
                });
            }

            window.addEventListener('open-enquiry-modal', () => {
                this.resetForm();
                setTimeout(() => this.checkFormValidity(), 50);
            });

            this.$nextTick(() => {
                const wrapper = this.$el.closest('[x-show]') || this.$el.closest('.modal-content');
                if (wrapper) {
                    new MutationObserver(() => {
                        if (wrapper.offsetParent !== null) setTimeout(() => this.checkFormValidity(), 100);
                    }).observe(wrapper, { attributes: true, attributeFilter: ['style', 'class'] });
                }
                setTimeout(() => this.checkFormValidity(), 100);
            });
        },
    };
}

window.itemEnquiryForm = itemEnquiryForm;
